import { z } from "zod";

/**
 * Partner Network: Logiful stellt Angebote von Partnern vor (Verpackung, Fotografie, Marketing, Finanzierung …).
 * Je Partner: Anfrage über Logiful (Lead mit Einwilligung, Partner bekommt eine Mail) oder direkter Link (Klick wird
 * gezählt). Logiful verdient fest je Anfrage und/oder prozentual am Abschluss; Kunden bekommen ggf. einen Vorteil.
 */
export const PARTNER_SIZES = ["s", "m", "l"] as const;
export type PartnerSize = (typeof PARTNER_SIZES)[number];
export const PARTNER_MODES = ["lead", "link"] as const;
export const PARTNER_LEAD_STATUSES = ["new", "contacted", "won", "lost"] as const;
export type PartnerLeadStatus = (typeof PARTNER_LEAD_STATUSES)[number];

const optionalUrl = z.union([z.literal(""), z.string().trim().url().max(500)]).nullable().optional().transform((v) => v || null);

export const partnerCommissionSchema = z.object({
  /** fester Betrag je Anfrage (€) */
  leadFeeEur: z.number().min(0).max(100_000).nullable().default(null),
  /** Anteil am Abschlusswert (%) */
  percent: z.number().min(0).max(100).nullable().default(null),
  /** Vereinbarung in Worten (nur Logiful) */
  note: z.string().trim().max(500).default(""),
});
export type PartnerCommission = z.infer<typeof partnerCommissionSchema>;

/** Zielgruppe: leer = alle Kunden; sonst nur Kunden mit einem dieser Kanäle bzw. diese Kunden */
export const partnerAudienceSchema = z.object({
  channelKinds: z.array(z.string().min(1)).max(20).default([]),
  tenantIds: z.array(z.string().uuid()).max(500).default([]),
});
export type PartnerAudience = z.infer<typeof partnerAudienceSchema>;

export const partnerInputSchema = z
  .object({
    name: z.string().trim().min(2, "Name fehlt").max(80),
    tagline: z.string().trim().max(140).default(""),
    description: z.string().trim().max(4000).default(""),
    category: z.string().trim().max(40).default("Sonstiges"),
    website: optionalUrl,
    size: z.enum(PARTNER_SIZES).default("s"),
    /** Akzentfarbe der Kachel (#RRGGBB) */
    accent: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Farbe als #RRGGBB").default("#1f2937"),
    /** Vorteil für Logiful-Kunden, z. B. „15 % Rabatt im ersten Jahr“ */
    benefit: z.string().trim().max(80).default(""),
    discountCode: z.string().trim().max(40).default(""),
    mode: z.enum(PARTNER_MODES).default("lead"),
    linkUrl: optionalUrl,
    /** Anfragen gehen an diese Adresse */
    leadEmail: z.union([z.literal(""), z.string().trim().email().max(200)]).nullable().optional().transform((v) => v || null),
    commission: partnerCommissionSchema.default({ leadFeeEur: null, percent: null, note: "" }),
    audience: partnerAudienceSchema.default({ channelKinds: [], tenantIds: [] }),
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(-1000).max(1000).default(0),
  })
  .superRefine((p, ctx) => {
    if (p.mode === "link" && !p.linkUrl) ctx.addIssue({ code: "custom", path: ["linkUrl"], message: "Link zum Angebot fehlt" });
    if (p.mode === "lead" && !p.leadEmail) ctx.addIssue({ code: "custom", path: ["leadEmail"], message: "E-Mail für Anfragen fehlt" });
  });
export type PartnerInput = z.infer<typeof partnerInputSchema>;

/** Daten, die der Kunde mit seiner Anfrage freigibt */
export const partnerLeadInputSchema = z.object({
  message: z.string().trim().max(2000).default(""),
  contact: z.object({
    name: z.string().trim().min(2, "Name fehlt").max(120),
    email: z.string().trim().email("E-Mail prüfen").max(200),
    phone: z.string().trim().max(40).default(""),
    company: z.string().trim().max(160).default(""),
  }),
  /** ausdrückliche Einwilligung zur Weitergabe an den Partner */
  consent: z.literal(true, { message: "Bitte der Weitergabe an den Partner zustimmen" }),
});
export type PartnerLeadInput = z.infer<typeof partnerLeadInputSchema>;

/** Provision einer Anfrage: fester Betrag je Anfrage + bei Abschluss Anteil am Abschlusswert (alles in Cent) */
export function partnerCommissionMinor(terms: { leadFeeMinor: number | null; percent: number | null }, status: PartnerLeadStatus, dealValueMinor: number | null): number {
  const fee = terms.leadFeeMinor ?? 0;
  const share = status === "won" && dealValueMinor && terms.percent ? Math.round((dealValueMinor * terms.percent) / 100) : 0;
  return fee + share;
}

/** sieht dieser Kunde das Angebot? */
export function partnerVisibleFor(audience: PartnerAudience, tenant: { id: string; channelKinds: string[] }): boolean {
  if (audience.tenantIds.length && !audience.tenantIds.includes(tenant.id)) return false;
  if (audience.channelKinds.length && !audience.channelKinds.some((k) => tenant.channelKinds.includes(k))) return false;
  return true;
}
